import type { GeotabAddinApi, GeotabConfig } from './types';

// ── Runtime mode state ────────────────────────────────────────────────────
// In add-in mode, `addinApi` is set by addin.ts during initialize().
// In standalone mode, `standaloneConfig` is set after successful login.

let addinApi: GeotabAddinApi | null = null;
let standaloneConfig: GeotabConfig | null = null;

// Login name of the operator, captured in both modes. Used to attribute
// write operations (see the Reset Device audit comment).
let currentUserName = '';

export function setAddinApi(api: GeotabAddinApi): void {
  addinApi = api;
}

export function setCurrentUserName(userName: string): void {
  currentUserName = userName;
}

export function getCurrentUserName(): string {
  return currentUserName;
}

export function setStandaloneConfig(config: GeotabConfig): void {
  standaloneConfig = config;
}

export function getStandaloneConfig(): GeotabConfig | null {
  return standaloneConfig;
}

export function clearConfig(): void {
  addinApi = null;
  standaloneConfig = null;
  currentUserName = '';
}

// ── Error normalization ───────────────────────────────────────────────────
// Geotab reports faults as an object, never a string. Read paths tolerated the
// old `String(error)` collapse because they rarely fail, but writes (Set/Add)
// fail with actionable reasons — DuplicateException, GroupRelationViolated,
// security-clearance denials — that have to survive to the UI.

export function extractGeotabError(error: unknown): string {
  if (error == null) return 'Unknown Geotab API error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error !== 'object') return String(error);

  const e = error as Record<string, unknown>;

  // JSON-RPC fault shape: { message, code, name, data: { type }, errors: [{ name, message }] }
  const inner = Array.isArray(e.errors) && e.errors.length
    ? e.errors[0] as Record<string, unknown>
    : null;
  const data = (e.data && typeof e.data === 'object') ? e.data as Record<string, unknown> : null;

  const type = data?.type ?? inner?.name ?? e.name;
  const message = inner?.message ?? e.message ?? e.description;

  if (type && message && type !== message) return `${String(type)}: ${String(message)}`;
  if (message) return String(message);
  if (type) return String(type);

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * The Geotab exception type (e.g. "OverLimitException", "InvalidUserException"),
 * read from the same fault shape `extractGeotabError` parses. Kept separate so a
 * caller can branch on the exact exception (retry-worthy vs. not) instead of
 * pattern-matching the human-readable message string.
 */
function extractGeotabErrorName(error: unknown): string | undefined {
  if (error == null || typeof error !== 'object') return undefined;
  const e = error as Record<string, unknown>;
  const inner = Array.isArray(e.errors) && e.errors.length
    ? e.errors[0] as Record<string, unknown>
    : null;
  const data = (e.data && typeof e.data === 'object') ? e.data as Record<string, unknown> : null;
  const type = data?.type ?? inner?.name ?? e.name;
  return typeof type === 'string' ? type : undefined;
}

/**
 * A thrown Error whose `.name` is the actual Geotab exception type when one was
 * identifiable (e.g. "OverLimitException"), and whose `.retryAfterMs` carries the
 * server's own back-off hint when the transport exposes response headers
 * (standalone/fetch mode only — the add-in bridge never surfaces raw headers).
 */
export class GeotabApiError extends Error {
  retryAfterMs?: number;
  constructor(rawError: unknown, retryAfterMs?: number) {
    super(extractGeotabError(rawError));
    const name = extractGeotabErrorName(rawError);
    if (name) this.name = name;
    if (retryAfterMs !== undefined) this.retryAfterMs = retryAfterMs;
  }
}

// ── Unified API call ──────────────────────────────────────────────────────
// Both modes call this function. The caller never needs to know the mode.

export async function callGeotabAPI<T = unknown>(
  method: string,
  params: Record<string, unknown>
): Promise<T> {
  if (addinApi) {
    return callViaAddinApi<T>(method, params);
  }
  if (standaloneConfig) {
    return callViaFetch<T>(method, params, standaloneConfig);
  }
  throw new Error('No Geotab API connection established');
}

function callViaAddinApi<T>(
  method: string,
  params: Record<string, unknown>
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    addinApi!.call(
      method,
      params,
      (result) => resolve(result as T),
      (error) => reject(new GeotabApiError(error))
    );
  });
}

async function callViaFetch<T>(
  method: string,
  params: Record<string, unknown>,
  config: GeotabConfig
): Promise<T> {
  const response = await fetch(`https://${config.server}/apiv1`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      method,
      params: { ...params, credentials: config.credentials },
    }),
  });
  const result = await response.json() as { result?: T; error?: unknown };
  if (result.error) {
    const retryAfterHeader = response.headers.get('Retry-After');
    const retryAfterMs = retryAfterHeader ? Number(retryAfterHeader) * 1000 : undefined;
    throw new GeotabApiError(result.error, Number.isFinite(retryAfterMs) ? retryAfterMs : undefined);
  }
  return result.result as T;
}

// ── Standalone authentication helper ─────────────────────────────────────

export async function authenticate(
  server: string,
  database: string,
  userName: string,
  password: string
): Promise<GeotabConfig> {
  const response = await fetch(`https://${server}/apiv1`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      method: 'Authenticate',
      params: { userName, password, database },
    }),
  });
  const result = await response.json() as {
    result?: { credentials: { userName: string; database: string; sessionId: string } };
    error?: { message: string };
  };
  if (result.error) throw new Error(result.error.message || 'Authentication failed');
  const config: GeotabConfig = {
    server,
    credentials: result.result!.credentials,
  };
  setStandaloneConfig(config);
  setCurrentUserName(config.credentials.userName ?? userName);
  return config;
}
